import { Path, useForm } from "react-hook-form";
import { Box } from "@mui/material";
import FormRow from "components/form/FormRow";
import { MESSAGE, REGEX } from "constants/validate";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import CancelButton from "components/button/CancelButton";
import ButtonComponent from "components/button/ButtonComponent";
import { formatDateTime } from "utils";
import { memo, useCallback, useEffect, useMemo } from "react";
import { Item } from "../types/item";

// Constants moved outside component to prevent recreation
const STYLES = {
  field: {
    width: "100%",
    height: "48px",
    boxSizing: "border-box" as const,
    backgroundColor: "var(--white)",
    borderRadius: 0,
    border: "3px solid var(--silver-150)",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
} as const;

const STATUS_OPTIONS = [
  { value: "", label: "" },
  { value: 1, label: "有効" },
  { value: 0, label: "無効" },
];

// Define proper types for form fields
interface BaseFormField {
  label: string;
  fieldName: string;
  disabled: boolean;
}

interface FormFieldWithRules extends BaseFormField {
  rules?: any;
}

interface FormFieldWithType extends FormFieldWithRules {
  type?: string;
}

interface FormFieldWithOptions extends FormFieldWithType {
  options?: Array<{ value: number | string; label: string }>;
}

type FormField = FormFieldWithOptions;

const ITEM_FORM_FIELDS: FormField[] = [
  { label: "YH商品コード", fieldName: "item_cd", disabled: true },
  { label: "JANコード", fieldName: "jan_cd", disabled: true },
  { label: "ブランドコード", fieldName: "brand_cd", disabled: true },
  { label: "商品名", fieldName: "item_name", disabled: true },
  { label: "メーカー品番", fieldName: "maker_item_cd", disabled: true },
  { label: "サイズ", fieldName: "item_size", disabled: true },
  { label: "商品カテゴリ（大）", fieldName: "item_category_l", disabled: true },
  { label: "商品カテゴリ（中）", fieldName: "item_category_m", disabled: true },
  { label: "商品カテゴリ（小）", fieldName: "item_category_s", disabled: true },
  { label: "定価（税抜）", fieldName: "list_price", disabled: true },
  { label: "A表価格", fieldName: "a_price", disabled: true },
  { label: "メイン仕入先コード", fieldName: "main_vendor_cd", disabled: true },
  { label: "メイン仕入先原価", fieldName: "main_purchase_cost", disabled: true },
  { label: "発注先コード", fieldName: "shipping_dest_cd", disabled: true },
  { label: "廃番", fieldName: "waste_num_flag", disabled: true },
  {
    label: "ECフラグ",
    fieldName: "ec_flag",
    disabled: false,
    type: "select",
    options: STATUS_OPTIONS,
  },
  {
    label: "2次仕入先コード",
    fieldName: "vendor_cd_2nd",
    disabled: false,
    type: "number",
    rules: {
      pattern: {
        value: REGEX.HALFWIDTH,
        message: MESSAGE.HALFWIDTH,
      },
      maxLength: {
        message: MESSAGE.MAX_LENGTH(10),
        value: 10,
      },
    },
  },
  {
    label: "3次仕入先コード",
    fieldName: "vendor_cd_3rd",
    disabled: false,
    rules: {
      pattern: {
        value: REGEX.HALFWIDTH,
        message: MESSAGE.HALFWIDTH,
      },
      maxLength: {
        message: MESSAGE.MAX_LENGTH(10),
        value: 10,
      },
    },
  },
  {
    label: "発注単位",
    fieldName: "hatchu_unit",
    disabled: false,
    type: "number-float",
    rules: {
      pattern: {
        value: REGEX.NUMERIC_FORMAT(9, 0),
        message: MESSAGE.NUMERIC_FORMAT(9, 0),
      },
    },
  },
  {
    label: "有効/無効",
    fieldName: "status_flag",
    disabled: false,
    type: "select",
    options: STATUS_OPTIONS,
  },
];

interface ItemFormProps {
  visible: boolean;
  onClose: () => void;
  onHandleSubmit: (form: Item) => void;
  isLoading: boolean;
  itemDetail: Item;
}

// Memoized header component
interface ModalHeaderProps {
  itemDetail: Item;
  onClose: () => void;
}

const ModalHeader = memo<ModalHeaderProps>(({ itemDetail, onClose }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
    <Typography
      variant="h4"
      component="h2"
      sx={{
        fontWeight: "fontWeightMedium",
        color: "var(--gray-700)",
        fontSize: { xs: 28, md: 33 },
      }}
    >
      商品詳細
      <Typography component="br" sx={{ display: { xs: "block", md: "none" } }} />
      <Typography
        component="span"
        sx={{
          fontSize: { xs: 16, md: 21 },
          fontWeight: "fontWeightMedium",
          color: "var(--gray-700)",
          ml: { xs: 0, md: 3 },
        }}
      >
        最終更新日 {formatDateTime(itemDetail.mst_yh_item_updated_at)}
      </Typography>
    </Typography>
    <CancelButton onClose={onClose} />
  </Box>
));

ModalHeader.displayName = "ModalHeader";

// Memoized footer component
interface ModalFooterProps {
  isLoading: boolean;
  hasErrors: boolean;
  itemDetail: Item;
  onClose: () => void;
}

const ModalFooter = memo<ModalFooterProps>(({ isLoading, hasErrors, itemDetail, onClose }) => (
  <>
    <Box display="flex" flexDirection="column" rowGap="15px" marginTop={4}>
      <ButtonComponent
        disabled={isLoading || hasErrors}
        type="submit"
        label="更新"
        theme="dark"
        sx={{ height: "50px", minWidth: "185px", width: "28%" }}
      />
      <Typography sx={{ fontSize: { xs: 16, md: 21 } }} fontWeight="medium" color="var(--gray-600)">
        更新日 {formatDateTime(itemDetail.updated_at ?? "")}
      </Typography>
    </Box>
    <CancelButton sx={{ mt: 3 }} onClose={onClose} />
  </>
));

ModalFooter.displayName = "ModalFooter";

/**
 * ItemFormModal Component
 *
 * This component renders a modal form for updating client details.
 * It uses `react-hook-form` for form management and validation.
 * @param {ItemFormProps} props - The props for the component.
 * @param {boolean} props.visible - Controls the visibility of the modal.
 * @param {() => void} props.onClose - Callback function to close the modal.
 * @param {(form: Item) => void} props.onHandleSubmit - Callback function to handle form submission.
 * @param {boolean} props.isLoading - Indicates whether the form is in a loading state.
 * @param {Item} props.itemDetail - Item details for pre-filling the form fields.
 */
function ItemFormModal({ visible, onClose, onHandleSubmit, isLoading, itemDetail }: ItemFormProps) {
  // Memoized default values with proper type conversion
  const itemDefaultValues = useMemo(() => ({
      ...itemDetail,
      vendor_cd_2nd: itemDetail.vendor_cd_2nd ?? "",
      vendor_cd_3rd: itemDetail.vendor_cd_3rd ?? "",
      // Convert hatchu_unit to string to avoid null values in form inputs
      hatchu_unit:
        typeof itemDetail.hatchu_unit === "string"
          ? itemDetail.hatchu_unit
          : (itemDetail.hatchu_unit?.toString() ?? ""),
    } as unknown as Partial<Item>), [itemDetail]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Item>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: itemDefaultValues,
  });

  // Memoized submit handler
  const onSubmit = useCallback(
    (data: Item) => {
      if (isLoading || Object.keys(errors).length > 0) return;

      // Convert hatchu_unit back to number if it's a string
      const processedData = {
        ...data,
        hatchu_unit:
          typeof data.hatchu_unit === "string" && data.hatchu_unit !== ""
            ? Number(data.hatchu_unit)
            : data.hatchu_unit,
      };

      onHandleSubmit(processedData);
    },
    [isLoading, errors, onHandleSubmit],
  );

  // Memoized error state
  const hasErrors = Object.keys(errors).length > 0;

  // Memoized modal styles
  const modalSx = useMemo(
    () => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    [],
  );

  const boxSx = useMemo(
    () => ({
      maxHeight: "90vh",
      overflow: "auto",
      width: "80%",
      maxWidth: "870px",
      backgroundColor: "var(--white)",
      padding: { xs: 3, md: 4, xl: "30px 70px" },
      borderRadius: "21px",
      display: "flex",
      flexDirection: "column",
      rowGap: "20px",
    }),
    [],
  );

  // Effect to reset form when itemDetail changes
  useEffect(() => {
    if (itemDetail && itemDetail.item_cd) {
      reset(itemDefaultValues);
    }
  }, [itemDetail.item_cd, itemDefaultValues, reset]);

  // Memoized form fields to prevent recreation
  const formFields = useMemo(
    () =>
      ITEM_FORM_FIELDS.map((field) => (
        <FormRow
          key={field.fieldName}
          sxLabel={{ pl: 4, width: "40%" }}
          label={field.label}
          sxBoxField={{ width: "60%", flexGrow: 1 }}
          sxField={{ ...STYLES.field }}
          name={field.fieldName as Path<Item>}
          type={field.type as never}
          control={control}
          rules={field.rules ? field.rules : undefined}
          options={field.type === "select" ? field.options : undefined}
          disabled={field.disabled}
        />
      )),
    [control],
  );

  return (
    <Modal sx={modalSx} open={visible} onClose={onClose}>
      <Box sx={boxSx}>
        <ModalHeader itemDetail={itemDetail} onClose={onClose} />
        <form onSubmit={handleSubmit(onSubmit)}>
          {formFields}
          <ModalFooter
            isLoading={isLoading}
            hasErrors={hasErrors}
            itemDetail={itemDetail}
            onClose={onClose}
          />
        </form>
      </Box>
    </Modal>
  );
}

export default memo(ItemFormModal);
